import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, Bug, Copy, Download, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

interface ErrorEntry {
  id: string;
  timestamp: Date;
  nodeId: string;
  nodeName: string;
  errorType: string;
  message: string;
  stack?: string;
  context?: Record<string, any>;
  severity: 'error' | 'warning' | 'info';
}

export function ErrorDebugger() {
  const [errors, setErrors] = useState<ErrorEntry[]>([
    {
      id: '1',
      timestamp: new Date(Date.now() - 30000),
      nodeId: 'http-1',
      nodeName: 'HTTP Request',
      errorType: 'NetworkError',
      message: 'Failed to fetch from API endpoint',
      severity: 'error',
      context: {
        url: 'https://api.example.com/data',
        method: 'GET',
        statusCode: 0,
        timeout: 5000,
      },
      stack: 'Error: Network timeout\n    at XMLHttpRequest.onerror\n    at processRequest',
    },
  ]);

  const [selectedError, setSelectedError] = useState<ErrorEntry | null>(errors[0] || null);
  const [showStack, setShowStack] = useState(false);

  const handleCopyError = () => {
    const errorText = `
Error: ${selectedError?.message}
Type: ${selectedError?.errorType}
Node: ${selectedError?.nodeName} (${selectedError?.nodeId})
Time: ${selectedError?.timestamp.toLocaleString()}
${selectedError?.stack ? `\nStack:\n${selectedError.stack}` : ''}
${selectedError?.context ? `\nContext:\n${JSON.stringify(selectedError.context, null, 2)}` : ''}
    `.trim();

    navigator.clipboard.writeText(errorText).then(() => {
      toast.success('Error copied to clipboard');
    });
  };

  const handleDownloadLog = () => {
    const logContent = errors
      .map(
        (err) => `
[${err.timestamp.toISOString()}] ${err.severity.toUpperCase()}: ${err.message}
Node: ${err.nodeName} (${err.nodeId})
Type: ${err.errorType}
${err.context ? `Context: ${JSON.stringify(err.context)}` : ''}
${err.stack ? `Stack: ${err.stack}` : ''}
`
      )
      .join('\n---\n');

    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `error-log-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Error log downloaded');
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      case 'warning':
        return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
      case 'info':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      default:
        return 'bg-muted';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-amber-600" />;
      case 'info':
        return <AlertCircle className="w-5 h-5 text-blue-600" />;
      default:
        return <Bug className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-foreground mb-2">Error Debugger</h2>
        <p className="text-muted-foreground">Analyze and debug workflow errors</p>
      </div>

      <Tabs defaultValue="errors" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="errors">
            Errors ({errors.filter((e) => e.severity === 'error').length})
          </TabsTrigger>
          <TabsTrigger value="warnings">
            Warnings ({errors.filter((e) => e.severity === 'warning').length})
          </TabsTrigger>
          <TabsTrigger value="all">All ({errors.length})</TabsTrigger>
        </TabsList>

        {/* Errors Tab */}
        <TabsContent value="errors" className="space-y-4">
          {errors.filter((e) => e.severity === 'error').length === 0 ? (
            <Card className="p-6 text-center text-muted-foreground">
              <p className="text-sm">No errors detected. Your workflow is running smoothly!</p>
            </Card>
          ) : (
            <div className="grid lg:grid-cols-3 gap-4">
              {/* Error List */}
              <Card className="lg:col-span-1 overflow-hidden flex flex-col">
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold text-foreground text-sm">Error Log</h3>
                </div>
                <div className="overflow-y-auto flex-1 max-h-96">
                  {errors
                    .filter((e) => e.severity === 'error')
                    .map((error) => (
                      <button
                        key={error.id}
                        onClick={() => setSelectedError(error)}
                        className={`w-full text-left p-3 border-b border-border hover:bg-muted/50 transition-colors ${
                          selectedError?.id === error.id ? 'bg-muted' : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {getSeverityIcon(error.severity)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {error.nodeName}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {error.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(error.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                </div>
              </Card>

              {/* Error Details */}
              {selectedError && selectedError.severity === 'error' && (
                <Card className="lg:col-span-2 p-4 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">
                        {selectedError.message}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {selectedError.nodeName} • {new Date(selectedError.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <Badge className={getSeverityColor(selectedError.severity)}>
                      {selectedError.severity}
                    </Badge>
                  </div>

                  {/* Error Type */}
                  <div className="p-3 rounded border border-border bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Error Type</p>
                    <p className="font-mono text-sm text-foreground">{selectedError.errorType}</p>
                  </div>

                  {/* Context */}
                  {selectedError.context && (
                    <div className="p-3 rounded border border-border bg-muted/50">
                      <p className="text-xs text-muted-foreground mb-2">Context</p>
                      <div className="font-mono text-xs text-foreground overflow-x-auto max-h-32 overflow-y-auto bg-background rounded p-2">
                        <pre>{JSON.stringify(selectedError.context, null, 2)}</pre>
                      </div>
                    </div>
                  )}

                  {/* Stack Trace */}
                  {selectedError.stack && (
                    <div className="space-y-2">
                      <button
                        onClick={() => setShowStack(!showStack)}
                        className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                      >
                        {showStack ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                        Stack Trace
                      </button>
                      {showStack && (
                        <div className="p-3 rounded border border-border bg-muted/50">
                          <div className="font-mono text-xs text-foreground overflow-x-auto max-h-48 overflow-y-auto bg-background rounded p-2">
                            <pre>{selectedError.stack}</pre>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyError}
                      className="flex-1"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Error
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadLog}
                      className="flex-1"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Log
                    </Button>
                  </div>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Warnings Tab */}
        <TabsContent value="warnings">
          {errors.filter((e) => e.severity === 'warning').length === 0 ? (
            <Card className="p-6 text-center text-muted-foreground">
              <p className="text-sm">No warnings. Everything looks good!</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {errors
                .filter((e) => e.severity === 'warning')
                .map((error) => (
                  <Card key={error.id} className="p-4">
                    <div className="flex items-start gap-3">
                      {getSeverityIcon(error.severity)}
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{error.message}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {error.nodeName} • {new Date(error.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
            </div>
          )}
        </TabsContent>

        {/* All Tab */}
        <TabsContent value="all">
          {errors.length === 0 ? (
            <Card className="p-6 text-center text-muted-foreground">
              <p className="text-sm">No issues detected</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {errors.map((error) => (
                <Card key={error.id} className={`p-4 ${getSeverityColor(error.severity)}`}>
                  <div className="flex items-start gap-3">
                    {getSeverityIcon(error.severity)}
                    <div className="flex-1">
                      <p className="font-medium">{error.message}</p>
                      <p className="text-sm mt-1 opacity-75">
                        {error.nodeName} • {error.errorType}
                      </p>
                      <p className="text-xs mt-1 opacity-50">
                        {new Date(error.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Troubleshooting Tips */}
      <Card className="p-6 bg-blue-500/5 border-blue-500/20 space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Bug className="w-5 h-5" />
          Debugging Tips
        </h3>
        <ul className="text-sm text-muted-foreground space-y-2">
          <li>• Check the error context to understand what data was being processed</li>
          <li>• Review the stack trace to identify the exact line that failed</li>
          <li>• Validate your API endpoints and authentication credentials</li>
          <li>• Test individual nodes before running the full workflow</li>
          <li>• Review the execution logs for detailed step-by-step information</li>
        </ul>
      </Card>
    </div>
  );
}
